import React, { useEffect, useState } from "react";
import { sortBy } from "lodash";

import { Box, SelectChangeEvent, Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useTheme } from "@mui/material/styles";

import { FilterBox, StatementCard } from "@/components/atoms";
import { useInsightFilters } from "@/context";
import { InsightResult, StatementScore } from "@/types";
import { calculateStatementScore, filterScores, searchScores } from "@/utils";

interface StatementsViewProps {
  scores: InsightResult[];
}

export const StatementsView: React.FC<StatementsViewProps> = ({ scores }) => {
  const {
    filters: { scoreType, searchText }
  } = useInsightFilters();
  const theme = useTheme();

  const [selectedScores, setSelectedScores] = useState<InsightResult[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [selectedStatements, setSelectedStatements] = useState<string[]>([]);
  const [statementScores, setStatementScores] = useState<StatementScore[]>([]);
  const [minMax, setMinMax] = useState<number[]>([0, 1]);

  const scoreGroups = Array.from(
    new Set(scores.map(x => x.tag_group))
  ).sort().map(x => ({name: x, label: x}));

  useEffect(() => {
    setSelectedGroup('Beauty & Appearance');
  }, []);

  useEffect(() => {
    if (searchText !== '') {
      const updatedScores = searchScores(scores, searchText, scoreType.value);
      setSelectedScores(updatedScores);
      setSelectedStatements(Array.from(new Set(updatedScores.map(x => x.tag_category))).sort());
    } else if (selectedGroup !== '') {
      const updatedScores = filterScores(scores, selectedGroup, scoreType.value);
      setSelectedScores(updatedScores);
      setSelectedStatements(Array.from(new Set(updatedScores.map(x => x.tag_category))).sort());
    } else {
      setSelectedScores([]);
      setSelectedStatements([]);
    }
  }, [scores, selectedGroup, scoreType, searchText]);

  useEffect(() => {
    setStatementScores(
      selectedStatements.map((statement, idx) => {
        const statementData = selectedScores.filter(x => x.tag_category === statement);
        return calculateStatementScore(statement, statementData);
      })
    )
  }, [selectedStatements, selectedScores]);

  useEffect(() => {
    const maxScore = Math.max.apply(
      Math,
      statementScores.map(x => Number(x[scoreType.value as keyof StatementScore])),
    );
    const minScore = Math.max(1 - (maxScore - 1), 0);

    setMinMax([minScore, maxScore]);
  }, [statementScores, scoreType])

  const handleGroupSelect = (e: SelectChangeEvent) => {
    setSelectedGroup(e.target.value);
  }

  return (
    <>
      <Grid container size={12} spacing={2}>

        <Grid size={12} display="flex" flexDirection="row" justifyContent="space-between" alignItems="flex-end">
          {searchText === '' && (
            <FilterBox
              label="Group"
              value={selectedGroup}
              options={scoreGroups}
              onChange={handleGroupSelect}
              showSelected
              width={300}
            />
          )}
          <Stack direction="row" spacing={2}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box
                height={2}
                width={6}
                sx={{
                  backgroundColor: theme.palette.score.dark
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.score.dark
                }}
              >
                Disagree
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box
                height={2}
                width={6}
                sx={{
                  backgroundColor: theme.palette.score.main
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.score.main
                }}
              >
                Neither
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box
                height={2}
                width={6}
                sx={{
                  backgroundColor: theme.palette.score.light
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.score.light
                }}
              >
                Agree
              </Typography>
            </Stack>

          </Stack>
        </Grid>

        <Grid container size={12} spacing={2}>
          {sortBy(statementScores, scoreType.value).reverse().map((statement, variableIdx) => (
            <Grid key={variableIdx}>
              <StatementCard
                statement={statement}
                score={scoreType.value}
                minValue={minMax[0]}
                maxValue={minMax[1]}
              />
            </Grid>
          ))}
        </Grid>

      </Grid>
    </>
  );
};

export default StatementsView;
